import { Injectable } from "@angular/core";
import {
  HttpClient,
  HttpErrorResponse,
  HttpResponse,
  HttpResponseBase,
} from "@angular/common/http";
import { BehaviorSubject, Observable, of, Subscriber } from "rxjs";
import { Messages } from "../model/messages";
import { EnvironmentHelper } from "./env.helper";
import { Router } from "@angular/router";
import { AuthService } from "../auth.service";
import { Priority } from "../model/priority";
import { Labeler } from "../model/labeler";

@Injectable({
  providedIn: "root",
})
export class PrioritiesService implements Labeler {
  private apiUrl: string;
  private byIdUrl: string;
  public _currentPriorities: BehaviorSubject<Priority[]>;
  public selectedPriority: BehaviorSubject<Priority | null>;

  constructor(
    private http: HttpClient,
    private env: EnvironmentHelper,
    private router: Router,
    private auth: AuthService
  ) {
    this._currentPriorities = new BehaviorSubject<Priority[]>([]);
    this.selectedPriority = new BehaviorSubject<Priority | null>(null);
    this.apiUrl = `${env.apiBaseUrl}priority/`;
    this.byIdUrl = `${env.apiBaseUrl}priority/by-id/`;
  }

  public priorities(token: string): Observable<Messages> {
    return new Observable<Messages>((observer) => {
      this.http
        .get<any>(this.apiUrl, {
          headers: { Authorization: "Bearer " + token },
          observe: "response",
        })
        .subscribe(
          (response) => {
            this.receivePriorities(response, observer);
          },
          (error) => {
            this.receivePriorities(error, observer);
          }
        );
    });
  }

  public createPriority(
    token: string,
    priority: Priority
  ): Observable<Messages> {
    return new Observable<Messages>((observer) => {
      this.http
        .post<any>(this.apiUrl, priority, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => {
            this.receivePriority(response, observer);
          },
          (error) => {
            this.receivePriority(error, observer);
          }
        );
    });
  }

  public updatePriority(
    token: string,
    priority: Priority
  ): Observable<Messages> {
    const url = `${this.apiUrl}by-id/${priority.id}`;

    return new Observable<Messages>((observer) => {
      if (!priority.id) {
        observer.next(Messages.from({ priority: ["priority.id.required"] }));
        return;
      }

      this.http
        .put<any>(url, priority, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receivePriority(response, observer),
          (error) => this.receivePriority(error, observer)
        );
    });
  }

  public deletePriority(
    token: string,
    priorityId: number
  ): Observable<Messages> {
    const url = `${this.apiUrl}by-id/${priorityId}`;
    return new Observable<Messages>((observer) => {
      this.http
        .delete<any>(url, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receivePriority(response, observer),
          (error) => this.receivePriority(error, observer)
        );
    });
  }

  public getPriorityById(
    token: string,
    priorityId: number
  ): Observable<Messages> {
    return new Observable<Messages>((observer) => {
      this.http
        .get<any>(`${this.byIdUrl}${priorityId}`, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receivePriority(response, observer),
          (error) => this.receivePriority(error, observer)
        );
    });
  }

  private receivePriorities(
    response: HttpResponseBase,
    observer: Subscriber<Messages>
  ) {
    if (response.status === 200) {
      const data = (response as HttpResponse<Priority[]>)?.body;
      if (!data) {
        observer.next(Messages.from({ priority: ["priority.unknown"] }));
        return;
      }
      this._currentPriorities.next(data);
      observer.next(Messages.EMPTY);
    } else if (response.status === 401) {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "auth",
          "unauthorized.access."
        )
      );
    } else {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "priority",
          "priority.incomplete"
        )
      );
    }
  }

  private receivePriority(
    response: HttpResponseBase,
    observer: Subscriber<Messages>
  ) {
    if (response.status === 200) {
      const data = (response as HttpResponse<Priority>)?.body;
      if (!data) {
        observer.next(Messages.from({ priority: ["priority.unknown"] }));
        return;
      }
      this._currentPriorities.next([data]);
      observer.next(Messages.EMPTY);
    } else if (response.status === 204) {
      observer.next(Messages.EMPTY);
    } else if (
      response.status === 400 ||
      response.status === 401 ||
      response.status === 409 ||
      response.status === 500
    ) {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "priority",
          "priority.load.error."
        )
      );
    } else {
      observer.next(
        Messages.orElse(
          (response as HttpErrorResponse).error,
          "general",
          "an.unexpected.error.has.occurred."
        )
      );
    }
  }

  public selectFromRouter() {
    const segments = this.getLastSuccessfulNavigationSegments();

    if (segments && segments.length > 0) {
      const priorityId = segments[segments.length - 1];
      const token = this.auth.token;

      this.getPriorityById(token, Number(priorityId)).subscribe();
    } else {
      const emptyPriority = new Priority(0, "", "", 2);
      this.select(emptyPriority);
    }
  }

  private getLastSuccessfulNavigationSegments(): string[] | null {
    const segments =
      this.router.lastSuccessfulNavigation?.finalUrl?.root.children["primary"]
        .segments;
    return segments ? segments.map((segment) => segment.path) : null;
  }

  public select(priority: Priority) {
    this.selectedPriority.next(priority);
  }

  public get selectedPriorityObservable(): Observable<Priority | null> {
    return this.selectedPriority.asObservable();
  }

  public get prioritiesObservable(): Observable<Priority[]> {
    return this._currentPriorities;
  }

  public get prioritiesData(): Priority[] {
    return this._currentPriorities.value;
  }

  public get currentPriority(): Priority {
    const value = this.selectedPriority.value;
    return value || Priority.EMPTY;
  }

  public labelForId(id: number): string {
    let label = "-";
    if (this.prioritiesData?.length > 0) {
      const found = this.prioritiesData.filter(
        (priorities) => id === priorities.id
      );
      if (found.length > 0) {
        label = found[0].value;
      }
    }
    return label;
  }
  public makeLabel(id: number): Observable<string> {
    const observable = new BehaviorSubject("");

    if (this.prioritiesData?.length > 0) {
      const label = this.labelForId(id);
      observable.next(label);
    } else {
      this.priorities(this.auth.token).subscribe((m) => {
        observable.next(this.labelForId(id));
      });
    }
    return observable;
  }
  public colorForId(id: number): string {
    let color = "#ffffff";
    if (this.prioritiesData?.length > 0) {
      const found = this.prioritiesData.filter(
        (priorities) => id === priorities.id
      );
      if (found.length > 0) {
        color = found[0].color;
      }
    }
    return color;
  }
  public makeColor(id: number): Observable<string> {
    const observable = new BehaviorSubject("#000000");

    if (this.prioritiesData?.length > 0) {
      const color = this.colorForId(id);
      observable.next(color);
    } else {
      this.priorities(this.auth.token).subscribe((m) => {
        observable.next(this.colorForId(id));
      });
    }
    return observable;
  }

  public makeBackgroundColor(id: number): Observable<string> {
    return new BehaviorSubject("#6495ED");
  }

  public importanceForId(id: number): number {
    let importance = 2;
    if (this.prioritiesData?.length > 0) {
      const found = this.prioritiesData.filter(
        (priorities) => id === priorities.id
      );
      if (found.length > 0) {
        importance = found[0].importance;
      }
    }
    return importance;
  }

  public makeImportance(id: number): Observable<number> {
    const observable = new BehaviorSubject(2);

    if (this.prioritiesData?.length > 0) {
      const importance = this.importanceForId(id);
      observable.next(importance);
    } else {
      this.priorities(this.auth.token).subscribe((m) => {
        observable.next(this.importanceForId(id));
      });
    }
    return observable;
  }
}
